import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ VEN-01 at v1.951, Phase 1's half: a list of places — name, address, whether stock may be
// kept there. A quote names one; its job copies it.

export type Venue = components['schemas']['VenueView'];
/** VEN-01 at v1.9101: a venue as the list gives it — with rooms, locality and (prices) billing. */
export type VenueListItem = components['schemas']['VenueListItemView'];
export type Room = components['schemas']['RoomView'];
export type VenueContact = components['schemas']['VenueContactView'];
export type VenueKind = Venue['kind'];
export type VenueRelationship = Venue['relationship'];
export type ContactRole = VenueContact['roles'][number];
export type VenueAccess = components['schemas']['VenueAccessView'];
export type DeliveryHours = VenueAccess['deliveryHours'][number];
/** What the access form sends: every field said, since the API replaces access whole. */
export type VenueAccessInput = { [K in keyof VenueAccess]-?: Exclude<VenueAccess[K], undefined> };
export type AccessMode = components['schemas']['VenueSettingsView']['mode'];

/** ISO weekdays, 1 Monday to 7 Sunday. */
export const WEEKDAYS = [
  '',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const ACCESS_MODE_LABELS: Record<AccessMode, string> = {
  fields_and_notes: 'Standard fields and notes',
  notes_only: 'Notes only',
  fields_only: 'Standard fields only',
};

export interface VenueInput {
  name: string;
  address?: string | null;
  holdsStock?: boolean;
  /** CMP-05 / VEN-05 at v1.965: the induction crew need here, and how many months it lasts. */
  induction?: { name: string; validMonths: number } | null;
  /** VEN-01 and VEN-07 at v1.971. */
  kind?: VenueKind;
  musterPoint?: string | null;
  /** VEN-02 at v1.973: the zone its local hours are read in; null means the company's. */
  timeZone?: string | null;
}

/** VEN-01 at v1.971: what the company knows privately about a venue. */
export interface VenueProfileChanges {
  relationship?: VenueRelationship;
  /** Null returns it to the default for the venue's kind. */
  listed?: boolean | null;
  notes?: string | null;
  /** True records a survey now, by you; false clears it. */
  surveyed?: boolean;
  /** VEN-11 at v1.988: the organisation the venue is invoiced as; null clears it. */
  billedAsClientId?: string | null;
}

export interface ContactInput {
  name: string;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  roles: ContactRole[];
}

// The words the office uses, in one place so every screen says the same thing.
export const KIND_LABELS: Record<VenueKind, string> = {
  venue: 'Venue',
  client_premises: 'Client’s premises',
  outdoor: 'Outdoor site',
  private: 'Private residence',
};

export const RELATIONSHIP_LABELS: Record<VenueRelationship, string> = {
  resident: 'In-house',
  partner: 'Partner',
  visited: 'Visited',
  competitor_held: 'Another supplier is in-house',
};

export const ROLE_LABELS: Record<ContactRole, string> = {
  venue_manager: 'Venue manager',
  events_coordinator: 'Events coordinator',
  technical: 'Technical',
  dock_security: 'Dock / security',
  emergency: 'Emergency',
};

/**
 * VEN-01 at v1.971: the venues a picker offers — your own first, then the rest of the listed ones,
 * each by name. An unlisted venue appears only when it is the one already chosen.
 */
export function pickerVenues(venues: readonly Venue[], chosenId: string | null): Venue[] {
  const shown = venues.filter((v) => v.listed || v.id === chosenId);
  return [...shown.filter((v) => v.mine), ...shown.filter((v) => !v.mine)];
}

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const venuesQuery = queryOptions({
  queryKey: ['venues'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/venues');
    if (error) throw problem(error, 'Could not load the venues.');
    return data;
  },
});

export async function createVenue(input: VenueInput) {
  const { data, error } = await api.POST('/v1/venues', { body: input });
  if (error) throw problem(error, 'Could not add that venue.');
  return data;
}

export const venueQuery = (id: string) =>
  queryOptions({
    queryKey: ['venues', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}', { params: { path: { id } } });
      if (error) throw problem(error, 'Could not load that venue.');
      return data;
    },
  });

export async function setMine(id: string, mine: boolean) {
  const { data, error } = mine
    ? await api.PUT('/v1/venues/{id}/mine', { params: { path: { id } } })
    : await api.DELETE('/v1/venues/{id}/mine', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not change your venues.');
  return data;
}

export const contactsQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'contacts'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/contacts', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load the contacts.');
      return data;
    },
  });

export async function addContact(venueId: string, input: ContactInput) {
  const { data, error } = await api.POST('/v1/venues/{id}/contacts', {
    params: { path: { id: venueId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not add that contact.');
  return data;
}

export async function updateContact(
  venueId: string,
  contactId: string,
  changes: Partial<ContactInput>,
) {
  const { data, error } = await api.PATCH('/v1/venues/{id}/contacts/{contactId}', {
    params: { path: { id: venueId, contactId } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not change that contact.');
  return data;
}

export async function retireContact(venueId: string, contactId: string) {
  const { error } = await api.DELETE('/v1/venues/{id}/contacts/{contactId}', {
    params: { path: { id: venueId, contactId } },
  });
  if (error) throw problem(error, 'Could not remove that contact.');
}

export async function updateVenue(id: string, changes: Partial<VenueInput> & VenueProfileChanges) {
  const { data, error } = await api.PATCH('/v1/venues/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not change that venue.');
  return data;
}

export async function setQuoteVenue(quoteId: string, venueId: string | null) {
  const { data, error } = await api.PUT('/v1/quotes/{id}/venue', {
    params: { path: { id: quoteId } },
    body: { venueId },
  });
  if (error) throw problem(error, 'Could not change the venue.');
  return data;
}

/** CB-REQ VEN-04 at v1.958: a venue's rooms, by name. */
export const roomsQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'rooms'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/rooms', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load the rooms.');
      return data;
    },
  });

export async function addRoom(venueId: string, name: string) {
  const { data, error } = await api.POST('/v1/venues/{id}/rooms', {
    params: { path: { id: venueId } },
    body: { name },
  });
  if (error) throw problem(error, 'Could not add that room.');
  return data;
}

/** CB-REQ VEN-02 at v1.973: how to get in and what fits. */
export const accessQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'access'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/access', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load the access details.');
      return data;
    },
  });

export async function setAccess(venueId: string, access: VenueAccessInput) {
  const { data, error } = await api.PUT('/v1/venues/{id}/access', {
    params: { path: { id: venueId } },
    body: access,
  });
  if (error) throw problem(error, 'Could not save the access details.');
  return data;
}

export async function updateRoom(venueId: string, roomId: string, accessNotes: string | null) {
  const { data, error } = await api.PATCH('/v1/venues/{id}/rooms/{roomId}', {
    params: { path: { id: venueId, roomId } },
    body: { accessNotes },
  });
  if (error) throw problem(error, 'Could not save the room’s note.');
  return data;
}

// CB-REQ VEN-04 at v1.976 (venue slice 2A): a room in depth.
export type RoomDetail = components['schemas']['RoomDetailView'];
export type RoomLayout = RoomDetail['capacities'][number]['layout'];
export const ROOM_LAYOUTS: ReadonlyArray<[RoomLayout, string]> = [
  ['theatre', 'Theatre'],
  ['banquet', 'Banquet'],
  ['cabaret', 'Cabaret'],
  ['classroom', 'Classroom'],
  ['boardroom', 'Boardroom'],
  ['standing', 'Standing'],
];
export const BLACKOUT_LABELS: Record<NonNullable<RoomDetail['blackout']>, string> = {
  full: 'Full blackout',
  partial: 'Partial blackout',
  none: 'No blackout',
};
// CB-REQ VEN-03 (venue slice 2B): a room's power and rigging.
export type Outlet = RoomDetail['outlets'][number];
export type OutletKind = Outlet['kind'];
export const OUTLET_KINDS: ReadonlyArray<[OutletKind, string]> = [
  ['3ph_32a', 'Three-phase 32 A'],
  ['3ph_20a', 'Three-phase 20 A'],
  ['1ph_15a', 'Single-phase 15 A'],
  ['1ph_10a', 'Single-phase 10 A'],
  ['other', 'Other'],
];

export async function setRoomOutlets(
  venueId: string,
  roomId: string,
  outlets: Array<{
    kind: OutletKind;
    count: number;
    location: string | null;
    notes: string | null;
  }>,
) {
  const { data, error } = await api.PUT('/v1/venues/{id}/rooms/{roomId}/power', {
    params: { path: { id: venueId, roomId } },
    body: { outlets },
  });
  if (error) throw problem(error, 'Could not save the room’s power.');
  return data;
}

export type RoomChanges = {
  name?: string;
  lengthMm?: number | null;
  widthMm?: number | null;
  heightMm?: number | null;
  blackout?: NonNullable<RoomDetail['blackout']> | null;
  obstructions?: string | null;
  riggingHeightMm?: number | null;
  riggingPoints?: number | null;
  riggingPointSwlKg?: number | null;
  houseRiggingMandatory?: boolean;
  houseMotorsMandatory?: boolean;
  riggingNotes?: string | null;
  generatorNotes?: string | null;
  networkNotes?: string | null;
};

export async function updateRoomDetails(venueId: string, roomId: string, changes: RoomChanges) {
  const { data, error } = await api.PATCH('/v1/venues/{id}/rooms/{roomId}', {
    params: { path: { id: venueId, roomId } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save the room.');
  return data;
}

export async function setRoomCapacities(
  venueId: string,
  roomId: string,
  capacities: Array<{ layout: RoomLayout; seats: number }>,
) {
  const { data, error } = await api.PUT('/v1/venues/{id}/rooms/{roomId}/capacities', {
    params: { path: { id: venueId, roomId } },
    body: { capacities },
  });
  if (error) throw problem(error, 'Could not save what the room seats.');
  return data;
}

export async function setRoomParts(venueId: string, roomId: string, partIds: string[]) {
  const { data, error } = await api.PUT('/v1/venues/{id}/rooms/{roomId}/parts', {
    params: { path: { id: venueId, roomId } },
    body: { partIds },
  });
  if (error) throw problem(error, 'Could not save the rooms it combines.');
  return data;
}

export async function retireRoom(venueId: string, roomId: string) {
  const { error } = await api.DELETE('/v1/venues/{id}/rooms/{roomId}', {
    params: { path: { id: venueId, roomId } },
  });
  if (error) throw problem(error, 'Could not retire the room.');
}

// CB-REQ VEN-04 and VEN-08 at v1.976 (venue slice 2C): a venue's plans and photos.
export type VenueFile = components['schemas']['VenueFileView'];
export type VenueFileKind = VenueFile['kind'];
export type PhotoTopic = NonNullable<VenueFile['topic']>;
export const VENUE_FILE_KINDS: ReadonlyArray<[VenueFileKind, string]> = [
  ['photo', 'Photo'],
  ['floor_plan', 'Floor plan'],
  ['rigging_plan', 'Rigging plan'],
];
export const PHOTO_TOPICS: ReadonlyArray<[PhotoTopic, string]> = [
  ['dock', 'The dock'],
  ['power', 'Power'],
  ['setup', 'A setup'],
  ['room', 'The room'],
  ['other', 'Something else'],
];

export const venueFilesQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'files'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/files', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load the plans and photos.');
      return data;
    },
  });

export async function uploadVenueFile(
  venueId: string,
  file: File,
  fields: { kind: VenueFileKind; roomId: string | null; topic: PhotoTopic | null; caption: string },
) {
  const data = new FormData();
  data.append('file', file);
  data.append('kind', fields.kind);
  data.append('roomId', fields.roomId ?? '');
  data.append('topic', fields.topic ?? '');
  data.append('caption', fields.caption);
  const { data: kept, error } = await api.POST('/v1/venues/{id}/files', {
    params: { path: { id: venueId } },
    body: data as never,
    bodySerializer: (body) => body as unknown as FormData,
  });
  if (error) throw problem(error, 'Could not upload that file.');
  return kept;
}

export async function captionVenueFile(venueId: string, recordId: string, caption: string | null) {
  const { error } = await api.PATCH('/v1/venues/{id}/files/{recordId}', {
    params: { path: { id: venueId, recordId } },
    body: { caption },
  });
  if (error) throw problem(error, 'Could not save the caption.');
}

export async function retireVenueFile(venueId: string, recordId: string) {
  const { error } = await api.DELETE('/v1/venues/{id}/files/{recordId}', {
    params: { path: { id: venueId, recordId } },
  });
  if (error) throw problem(error, 'Could not retire that file.');
}

/** VEN-02 at v1.971: how the company records access — a company setting. */
export const venueSettingsQuery = queryOptions({
  queryKey: ['venue-settings'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/venue-settings');
    if (error) throw problem(error, 'Could not load the venue settings.');
    return data;
  },
});

export async function setAccessMode(mode: AccessMode) {
  const { data, error } = await api.PUT('/v1/venue-settings', { body: { mode } });
  if (error) throw problem(error, 'Could not change how access is recorded.');
  return data;
}

/** INV-A14 at v1.982: whether the company records the equipment venues own. A company setting. */
export async function setVenueEquipment(venueEquipment: boolean) {
  const { data, error } = await api.PUT('/v1/venue-settings', { body: { venueEquipment } });
  if (error) throw problem(error, 'Could not change whether venue equipment is recorded.');
  return data;
}

/** INV-A14 at v1.983: one of the venue's own items its hire includes. */
export type Inclusion = components['schemas']['InclusionView'];

export const inclusionsQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'inclusions'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/inclusions', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load what the venue includes.');
      return data;
    },
  });

export async function addInclusion(
  venueId: string,
  input: { productId: string; roomId: string | null; quantity: number },
) {
  const { data, error } = await api.POST('/v1/venues/{id}/inclusions', {
    params: { path: { id: venueId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not include that.');
  return data;
}

export async function setInclusionQuantity(venueId: string, inclusionId: string, quantity: number) {
  const { data, error } = await api.PATCH('/v1/venues/{id}/inclusions/{inclusionId}', {
    params: { path: { id: venueId, inclusionId } },
    body: { quantity },
  });
  if (error) throw problem(error, 'Could not change how many are included.');
  return data;
}

export async function retireInclusion(venueId: string, inclusionId: string) {
  const { error } = await api.DELETE('/v1/venues/{id}/inclusions/{inclusionId}', {
    params: { path: { id: venueId, inclusionId } },
  });
  if (error) throw problem(error, 'Could not take that off what the venue includes.');
}
